import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import '../css/layout.styl'
import PaymentMethod from './PaymentMethod'
import PaymentMethodSidebar from '../components/PaymentMethodSidebar'
import { getUrlAssets } from '../constants'
import PaymentMethodConfirmation from '../components/PaymentMethodConfirmation'
import SuccessAnimation from '../components/SuccessAnimation'
import Modal from 'react-modal';
import { selectLoading as selectAuthLoading } from '../app/reducers/auth'
import { selectLoading as selectSessionLoading } from '../app/reducers/session'
import { selectLoading as selectOperationLoading, selectOperation, selectCaptureConfigurations } from '../app/reducers/operation'
import { selectLoading as selectPaymentMethodLoading, selectLoadingTransaction, 
  selectId as selectPaymentMethodId,
  selectResponseWithAnimation, selectNextAction,
  selectShowCvvHelp, setShowCvvHelp,
  selectIsMethodSwitch
} from '../app/reducers/paymentMethod'
import { selectList } from '../app/reducers/methodsList'
import { selectLoading as selectAuthenticationLoading, selectLoadingAuthorization } from '../app/reducers/authentication'
import { selectSettings } from '../app/reducers/flexConfiguration';
import { setOperationChange } from '../app/reducers/paymentMethodChange';
import { selectLanguage, setLanguage, selectLanguages, selectLanguageMode } from '../app/reducers/language';
import { useTranslation } from '../hooks/useTranslation';

const getInitialResolution = () => {
  const w = window.innerWidth;
  if (w >= 1536) return '2xl';
  if (w >= 1280) return 'xl';
  if (w >= 1024) return 'lg';
  if (w >= 768) return 'md';
  if (w >= 640) return 'sm';
  return 'xs';
};

export default function PaymentUI({responseCallback, trackingCallback, onErrorCallback}){
  const [resolution, setResolution] = useState(getInitialResolution);
  const [collapsed, setCollapsed] = useState(true);
  const containerRef = useRef(null);
  const modalContainerRef = useRef(null);
  const collapsedRef = useRef(true);
  collapsedRef.current = collapsed;
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!collapsedRef.current) return; // skip resize when sidebar is expanded to prevent infinite loop
      for (let entry of entries) {
        let w = entry.contentRect.width;
        if(w>=1536){
          setResolution('2xl');
        }else if(w>=1280){
          setResolution('xl');
        }else if(w>=1024){
          setResolution('lg');
        }else if(w>=768){
          setResolution('md');
        }else if(w>=640){
          setResolution('sm');
        }else{
          setResolution('xs');
        }
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect(); // clean up 
  }, []);
  const dispatch = useDispatch();
  const operationData = useSelector(selectOperation);
  const captureConfigurations = useSelector(selectCaptureConfigurations);
  const authLoading = useSelector(selectAuthLoading);
  const sessionLoading = useSelector(selectSessionLoading);
  const operationLoading = useSelector(selectOperationLoading);
  const paymentMethodLoading = useSelector(selectPaymentMethodLoading);
  const isMethodSwitch = useSelector(selectIsMethodSwitch);
  const transactionLoading = useSelector(selectLoadingTransaction);
  const authenticationLoading = useSelector(selectAuthenticationLoading);
  const authorizationLoading = useSelector(selectLoadingAuthorization);
  let isPreloading = authLoading || sessionLoading ;
  let isLoading = authLoading ||
                    sessionLoading ||
                    operationLoading ||
                    (!isMethodSwitch && paymentMethodLoading) ||
                    transactionLoading ||
                    authenticationLoading ||
                    authorizationLoading;
  const methodsList = useSelector(selectList);
  const methodId = useSelector(selectPaymentMethodId);
  const nextAction = useSelector(selectNextAction);
  const chooseMethod = (method, operation) => {
    setCollapsed(true);
    console.log({methodChoosed:method, operation:operation});
    dispatch(setOperationChange({
      operation: operation,
      paymentMethod: method
    }));
  };
  const responseWithAnimation = useSelector(selectResponseWithAnimation);
  const configSettings = useSelector(selectSettings);
  const language = useSelector(selectLanguage);
  const availableLanguages = useSelector(selectLanguages);
  const languageMode = useSelector(selectLanguageMode);
  const { t, tMixed } = useTranslation();
  const showCvvHelp = useSelector(selectShowCvvHelp);
  const cvvDialogRef = useRef(null);
  const hideResultScreen = () => {
    return (!configSettings.display_result_screen && nextAction && nextAction.type=="execute_response_callback" && responseWithAnimation);
  }
  useEffect(() => {
    const dialog = cvvDialogRef.current;
    if (!dialog) return;
    if (showCvvHelp && !dialog.open) {
      dialog.showModal();
    } else if (!showCvvHelp && dialog.open) {
      dialog.close();
    }
  }, [showCvvHelp]);
  return(
    <div className={"payment-gateway-ui"} ref={containerRef}>
      <div className={"payment-gateway-modal-container"} ref={modalContainerRef}></div>
      {false && <Modal
        isOpen={!isLoading}
        contentLabel="Example Modal"
        parentSelector={() => modalContainerRef.current}
      >
        <div>I am a modal</div>
      </Modal>}
      <div className={"loading-overlay "+(isLoading ? 'is-loading' : 'is-not-loading')}>
        <div className={"loading-detail clear-loader"}>
          <div className={"loading-icon-clear"}></div>
          <img src={getUrlAssets()+"/payme-logo.svg"} className={"loading-logo"} alt="Pay-me" />
        </div>
      </div>
      <PaymentMethodConfirmation />
      <SuccessAnimation />
      <div className={"payment-gateway-container payui-resolution-"+resolution+(collapsed?"":" expanded")+(hideResultScreen()?" hide-result-screen":"")+(configSettings.show_border===false?" no-border":"")+(isPreloading?"":" content-ready")} >
        {(configSettings.merchant_logo || captureConfigurations.merchant_logo || (languageMode !== 'single' && availableLanguages.length > 0) || configSettings.show_close_button) && <div className={"payment-header"}>
          {(configSettings.merchant_logo || captureConfigurations.merchant_logo) && <img src={configSettings.merchant_logo || captureConfigurations.merchant_logo} className={"merchant-logo"} alt="Logo del comercio"/>}
          <div className={"payment-header-actions"}>
            {languageMode !== 'single' && availableLanguages.length > 0 && <div className={"language-selector"}>
              <span className={"language-selector-indicator"} style={{transform: `translateX(${availableLanguages.indexOf(language) * 100}%)`}} />
              {availableLanguages.map((lang) => (
                <span 
                  key={lang}
                  className={'language-option' + (language === lang ? ' active' : '')} 
                  onClick={() => dispatch(setLanguage(lang))}
                >
                  {lang.toUpperCase()}
                </span>
              ))}
            </div>}
            {configSettings.show_close_button && <div className={"close-btn"} role="button" tabIndex={0} onClick={()=>{onErrorCallback({"message":"Cerro el carrito"})}} onKeyDown={(e)=>{if(e.key==='Enter'||e.key===' ')onErrorCallback({"message":"Cerro el carrito"})}}>
              <span className={"close-btn-x"}>✕</span>
            </div>}
          </div>
        </div>}
        {/* select-payment-message movido a PaymentMethodSidebar */}
        <div className={"payment-methods-container"}>
          {methodsList.length > 1 && <PaymentMethodSidebar methodsList={methodsList} methodId={methodId} chooseMethod={chooseMethod} resolution={resolution} collapsed={collapsed} setCollapsed={setCollapsed} />}
          {methodsList.map((value,pos) => (<PaymentMethod key={pos} pos={pos} method={value} chooseMethod={chooseMethod} methodChoosed={value.id==methodId} responseCallback={responseCallback} trackingCallback={trackingCallback} onErrorCallback={onErrorCallback} />))}
          {/* --- Antes: los botones se renderizaban dentro de cada PaymentMethod y el collapse btn estaba aquí ---
          {resolution=="xs" && methodsList.length > 1 && 
            <div className={"payment-method-wrapper collapse-btn"}>
              <div className={"payment-choose-btn choose-btn-"+(1+methodsList.length)} onClick={()=>{setCollapsed(!collapsed)}}>
                <img src={getUrlAssets()+(collapsed?"/sandwich.png":"/close.png")}
                  className={"sm-nav-payment-method-icon"}/>
              </div>
            </div>}
          */}
        </div>
        {captureConfigurations.show_operation_number && <div className={"payment-operation-number"}>{operationData ? tMixed(captureConfigurations.show_operation_number_prefix)+operationData.merchant_operation_number : ""}</div>}
      </div>
      <dialog className={"cvv-help-dialog"} ref={cvvDialogRef} onClose={()=>dispatch(setShowCvvHelp(false))}>
        <div className={"cvv-help-modal"}>
          <div className={"cvv-help-title"}>{t('payment_method.cvv_help_title')}</div>
          <div className={"cvv-help-text"}>{t('payment_method.cvv_help_text')}</div>
          <div className={"cvv-help-cards"}>
            <img src={getUrlAssets()+"/cvv_help.png"} alt="Ayuda CVV" />
          </div>
          <button className={"cvv-help-btn"} onClick={()=>dispatch(setShowCvvHelp(false))}>{t('payment_method.cvv_help_button')}</button>
        </div>
      </dialog>
    </div>
  );
}